import { ComponentProps } from "react";
import { RouteFactory } from "@app/routePaths";
import { getEnvStoryQueueId } from "@caas/getEnvStoryQueueId";
import E50AboutPage from "@e50/components/E50AboutPage";
import E50AwardsPage from "@e50/components/E50AwardsPage";
import E50ContactUsPage from "@e50/components/E50ContactUsPage";
import E50Footer from "@e50/components/E50FooterComponents/E50Footer";
import E50FAQPage from "@e50/components/FAQ/E50FaqPage";
import E50HowToNominatePage from "@e50/components/HowToNominate/E50HowToNominatePage";
import EEAboutPage from "@ee/components/EEAboutPage";
import EEApplyNowPage from "@ee/components/EEApplyNowPage";
import EEAwardsPage from "@ee/components/EEAwardsPage";
import EEContactUsPage from "@ee/components/EEContactUsPage";
import EEFooter from "@ee/components/EEFooterComponents/EEFooter";
import EEFAQPage from "@ee/components/FAQ/EEFAQPage";
import EEHowToNominatePage from "@ee/components/HowToNominate/EEHowToNominatePage";
import EEPartnersPage from "@ee/components/Partners/EEPartnersPage";
import { AWARDS_EVENTS_PAGE_ROW_DATA } from "@events-awards/utils/constants";
import { AwardType, Winner } from "@events-awards/utils/types";
import { TabType } from "@hooks/useTabs";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import BusinessmanAward from "@sba/components/BusinessmanAward";
import ChiefExecutiveAward from "@sba/components/ChiefExecutiveAward";
import EnterpriseAward from "@sba/components/EnterpriseAward";
import SBAHowToNominatePage from "@sba/components/HowToNominate/SBAHowToNominatePage";
import OverseasExecutiveAward from "@sba/components/OverseasExecutiveAward";
import SBAAboutPage from "@sba/components/SBAAboutPage";
import SBAAwardsPage from "@sba/components/SBAAwardsPage";
import SBAContactUsPage from "@sba/components/SBAContactUs";
import SBAFooter from "@sba/components/SBAFooterComponents/SBAFooter";
import SBASingleAwardPage from "@sba/components/SBASingleAwardPage";
import YoungBusinessLeaderAward from "@sba/components/YoungBusinessLeaderAward";
import AnnualReportPage from "@sca/components/Awards/AnnualReportPage";
import ChiefExecutiveOfficerPage from "@sca/components/Awards/ChiefExecutiveOfficerPage";
import ChiefFinancialOfficerPage from "@sca/components/Awards/ChiefFinancialofficerPage";
import GovernanceAwardPage from "@sca/components/Awards/GovernanceAwardPage";
import InvestorRelationsPage from "@sca/components/Awards/InvestorRelationsPage";
import ManagedBoardPage from "@sca/components/Awards/ManagedBoardPage";
import RiskManagementPage from "@sca/components/Awards/RiskManagementPage";
import SpecialEditionPage from "@sca/components/Awards/SpecialEditionPage";
import SpecialRecognitionAwardPage from "@sca/components/Awards/SpecialRecognitionAwardPage";
import FAQPage from "@sca/components/FAQ/FAQPage";
import SCAHowToNominatePage from "@sca/components/HowToNominate/SCAHowToNominatePage";
import PartnersPage from "@sca/components/Partners/PartnersPage";
import SCAAboutPage from "@sca/components/SCAAboutPage";
import SCAAwardsPage from "@sca/components/SCAAwardsPage";
import SCAContactUsPage from "@sca/components/SCAContactUsPage";
import SCAFooter from "@sca/components/SCAFooterComponents/SCAFooter";
import SCASingleAwardPage from "@sca/components/SCASingleAwardPage";
import AssesmentSection from "@sia/components/AssesmentSection";
import DeclarationSection from "@sia/components/DeclarationSection";
import NominationsSection from "@sia/components/NominationsSection";
import SIAAboutPage from "@sia/components/SIAAboutPage";
import SIAAwardsPage from "@sia/components/SIAAwardsPage";
import SIAContactUsPage from "@sia/components/SIAContactUsPage";
import SIAFooter from "@sia/components/SIAFooter";
import SIAHowToNominatePage from "@sia/components/SIAHowToNominatePage";
import SIAJudgesPage from "@sia/components/SIAJudgesPage";
import SIAPartnersPage from "@sia/components/SIAPartnersPage";
import type { Element, SanitizedFeedSectionArticle } from "@typings/Cue";
import { ENVIRONMENT } from "@util/constant";
import dayjs from "dayjs";

export const getAwardType = (
  sectionUniqueName: string
): AwardType | undefined => {
  switch (sectionUniqueName) {
    case "events-awards_enterprise-50":
      return AwardType.E50;

    case "events-awards_emerging-enterprise":
      return AwardType.EE;

    case "events-awards_singapore-business-awards":
      return AwardType.SBA;

    case "events-awards_singapore-corporate-awards":
      return AwardType.SCA;

    case "events-awards_sustainability-impact-awards":
      return AwardType.SIA;

    case "events-awards_design-ai-and-tech-awards":
      return AwardType.DATA;
  }
};

export const EventsAwardsFooterFactory = (awardType: AwardType) => {
  switch (awardType) {
    case AwardType.SIA:
      return SIAFooter;

    case AwardType.SBA:
      return SBAFooter;

    case AwardType.SCA:
      return SCAFooter;

    case AwardType.E50:
      return E50Footer;

    case AwardType.EE:
      return EEFooter;
  }
};

/**
 * Helper function to change all dash to
 * space and capitalize every word
 * @param text
 * @returns toEventsAwardsMicrositeTitleCase
 */
export function toEventsAwardsMicrositeTitleCase(text: string) {
  return text
    .replace("-", " ") // Replace hyphen with space
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
}

export const eventsAwardsMainPageStoryQueueId = (
  awardType: AwardType
): string => {
  switch (awardType) {
    case AwardType.SBA:
      return getEnvStoryQueueId("1295782", "1295782", "2803541", ENVIRONMENT);

    case AwardType.SCA:
      return getEnvStoryQueueId("1295790", "1295790", "2803492", ENVIRONMENT);

    case AwardType.SIA:
      return getEnvStoryQueueId("1295781", "1295781", "2803552", ENVIRONMENT);

    case AwardType.E50:
      return getEnvStoryQueueId("1295479", "1295479", "2798972", ENVIRONMENT);

    case AwardType.EE:
      return getEnvStoryQueueId("1295783", "1295783", "1210671", ENVIRONMENT);

    case AwardType.DATA:
      return "";
  }
};

// Utility: Get Award Data
export const getAwardData = (awardType: AwardType) => {
  return AWARDS_EVENTS_PAGE_ROW_DATA.find(
    (data) => data.awardType === awardType
  );
};

export const processArticleElements = (elements: Element[]): Winner[] => {
  const awards: Winner[] = [];
  let currentAward: Winner = {};

  elements.forEach((element) => {
    if (element.type === "paragraph") {
      const value = element.fields[0]?.value;
      const isBold = element.fields[0]?.annotations?.some(
        (a) => a.name === "bold" && a.value === "true"
      );

      if (isBold) {
        // If we have a previous award, push it to awards array
        if (currentAward.title) {
          awards.push({ ...currentAward });
        }
        // Start a new award
        currentAward = { title: value, type: "paragraph" };
      } else {
        // Add details to current award
        if (!currentAward.recipient) {
          currentAward.recipient = value;
        } else if (!currentAward.position) {
          currentAward.position = value;
        } else if (!currentAward.organization) {
          currentAward.organization = value;
        }
      }
    }
    if (element.type === "table") {
      currentAward.recipient = element.fields[0]?.value;
      currentAward.type = "table";
    }
  });

  // Push the last award
  if (currentAward.title) {
    awards.push(currentAward);
  }

  return awards;
};

export type Image = Element & {
  type: "image";
  relation: {
    fields: {
      "landscape-caas": {
        url: string;
        width: number;
        height: number;
      };
    };
  };
};

export const isImageElement = (element: Element): element is Image => {
  return (
    element.type === "image" &&
    element.relation?.fields?.["landscape-caas"]?.url !== undefined
  );
};

export const getAllImages = (
  articles: SanitizedFeedSectionArticle[] = []
): Image[] => {
  return (
    articles?.flatMap(
      (article) => article.elements?.filter(isImageElement) ?? []
    ) ?? []
  );
};

/**
 * Maps an award slug to its corresponding AwardType enum value.
 * This function is used to convert URL friendly award names to their internal enum representation.
 *
 * @param awards - The URL friendly award name (e.g., 'enterprise-50', 'singapore-business-awards')
 * @returns The corresponding AwardType enum value, or null if no matching award type is found
 *
 * @example
 * mapAwardType('enterprise-50') // returns AwardType.E50
 * mapAwardType('unknown-award') // returns null
 */
export const mapAwardType = (awards: string): AwardType | null => {
  const awardTypeMap: Record<string, AwardType> = {
    "singapore-corporate-awards": AwardType.SCA,
    "enterprise-50": AwardType.E50,
    "emerging-enterprise": AwardType.EE,
    "singapore-business-awards": AwardType.SBA,
    "sustainability-impact-awards": AwardType.SIA,
    "design-ai-and-tech-awards": AwardType.DATA,
  };
  return awardTypeMap[awards] || null;
};

/**
 * Determines if a menu link should be marked as active based on the current URL.
 *
 * - Trims trailing slashes from both `menuLink` and `pathname` for comparison.
 * - Returns true if:
 *   - The current path matches the menu link exactly, or
 *   - The current path starts with the menu link and includes `/gallery/` (to handle nested routes like galleries).
 *
 * @param menuLink - The absolute path of the menu link/component (e.g., "/events").
 * @param pathname - The current URL path from the window location (e.g., "/events/gallery/2025").
 * @returns A boolean indicating whether the link is active.
 */
export const isActiveLink = (menuLink: string, pathname: string) => {
  const currentPath = pathname.replace(/\/$/, "");
  const baseMenuLink = menuLink.replace(/\/$/, "");

  return (
    currentPath === baseMenuLink ||
    (currentPath.startsWith(baseMenuLink) && currentPath.includes("/gallery/"))
  );
};

/**
 * Returns the corresponding static component to render based on the provided page identifier.
 *
 * This function is typically used to dynamically render different sections of the
 * Sustainability Impact Awards (SIA) pages like "about", "awards", or "how-to-nominate".
 *
 * @param page - The string identifier of the page (e.g., "about", "awards", "how-to-nominate").
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const renderSIAStaticComponents = (page: string) => {
  switch (page) {
    case "about":
      return SIAAboutPage;

    case "awards":
      return SIAAwardsPage;

    case "how-to-nominate":
      return SIAHowToNominatePage;

    case "contact-us":
      return SIAContactUsPage;

    case "partners":
      return SIAPartnersPage;

    case "judges":
      return SIAJudgesPage;
  }
};

/**
 * Returns the corresponding static component to render based on the provided page identifier.
 *
 * This function is typically used to dynamically render different sections of the
 * Sustainability Impact Awards (SBA) pages like "about", "awards", or "how-to-nominate".
 *
 * @param page - The string identifier of the page (e.g., "about", "awards", "how-to-nominate").
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const SBAStaticPageFactory = (page: string) => {
  switch (true) {
    case page === RouteFactory.sbaAboutPage:
      return SBAAboutPage;

    case page === RouteFactory.sbaAwardsPage:
      return SBAAwardsPage;

    case page === RouteFactory.sbaNominatePage:
      return SBAHowToNominatePage;

    case page === RouteFactory.sbaContactUsPage:
      return SBAContactUsPage;

    case page.startsWith("/events-awards/singapore-business-awards/awards/"):
      return SBASingleAwardPage;
  }
};

/**
 * Returns the corresponding static component to render based on the provided page identifier.
 *
 * This function is typically used to dynamically render different sections of the
 * Sustainability Impact Awards (EE) pages like "about", "awards", or "how-to-nominate".
 *
 * @param page - The string identifier of the page (e.g., "about", "awards", "how-to-nominate").
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const EEStaticPageFactory = (page: string) => {
  switch (true) {
    case page === RouteFactory.eeAboutPage:
      return EEAboutPage;

    case page === RouteFactory.eeAwardsPage:
      return EEAwardsPage;

    case page === RouteFactory.eeNominatePage:
      return EEHowToNominatePage;

    case page === RouteFactory.eeApplyNowPage:
      return EEApplyNowPage;

    case page === RouteFactory.eeContactUsPage:
      return EEContactUsPage;

    case page === RouteFactory.eePartnersPage:
      return EEPartnersPage;

    case page === RouteFactory.eeFaqPage:
      return EEFAQPage;
  }
};

/**
 * Returns the corresponding static component to render based on the provided page identifier.
 *
 * This function is typically used to dynamically render different sections of the
 * Sustainability Impact Awards (SCA) pages like "about", "awards", or "how-to-nominate".
 *
 * @param page - The string identifier of the page (e.g., "about", "awards", "how-to-nominate").
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const SCAStaticPageFactory = (page: string) => {
  switch (true) {
    case page === RouteFactory.scaAboutPage:
      return SCAAboutPage;

    case page === RouteFactory.scaAwardsPage:
      return SCAAwardsPage;

    case page === RouteFactory.scaNominatePage:
      return SCAHowToNominatePage;

    case page === RouteFactory.scaContactUsPage:
      return SCAContactUsPage;

    case page === RouteFactory.scaFaqPage:
      return FAQPage;

    case page === RouteFactory.scaPartnersPage:
      return PartnersPage;

    case page.startsWith("/events-awards/singapore-corporate-awards/awards/"):
      return SCASingleAwardPage;
  }
};

/**
 * Returns the corresponding static component to render based on the provided page identifier.
 *
 * This function is typically used to dynamically render different sections of the
 * Sustainability Impact Awards (E50) pages like "about", "awards", or "how-to-nominate".
 *
 * @param page - The string identifier of the page (e.g., "about", "awards", "how-to-nominate").
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const E50StaticPageFactory = (page: string) => {
  switch (true) {
    case page === RouteFactory.e50AboutPage:
      return E50AboutPage;

    case page === RouteFactory.e50AwardsPage:
      return E50AwardsPage;

    case page === RouteFactory.e50NominatePage:
      return E50HowToNominatePage;

    case page === RouteFactory.e50ContactUsPage:
      return E50ContactUsPage;

    case page === RouteFactory.e50FaqPage:
      return E50FAQPage;
  }
};

/**
 * Returns the corresponding static component to render based on the provided active tab identifier.
 *
 * This function is typically used to dynamically render different sections of the
 * Singapore Business Awards (SBA) Awards pages.
 *
 * @param page - The string identifier of the active tab.
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const SBAAwardsTabFactory = (tab: TabType) => {
  switch (tab) {
    case "businessman-of-the-year":
      return BusinessmanAward;

    case "outstanding-chief-executive-of-the-year":
      return ChiefExecutiveAward;

    case "the-enterprise-award":
      return EnterpriseAward;

    case "outstanding-overseas-executive-of-the-year":
      return OverseasExecutiveAward;

    case "young-business-leader-of-the-year":
      return YoungBusinessLeaderAward;
  }
};

/**
 * Returns the corresponding static component to render based on the provided active tab identifier.
 *
 * This function is typically used to dynamically render different sections of the
 * Singapore Business Awards (SCA) Awards pages.
 *
 * @param page - The string identifier of the active tab.
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const SCAAwardsTabFactory = (tab: TabType) => {
  switch (tab) {
    case "best-managed-board":
      return ManagedBoardPage;

    case "best-chief-executive-officer":
      return ChiefExecutiveOfficerPage;

    case "best-chief-financial-officer":
      return ChiefFinancialOfficerPage;

    case "best-risk-management":
      return RiskManagementPage;

    case "best-investors-relations":
      return InvestorRelationsPage;

    case "best-annual-report":
      return AnnualReportPage;

    case "special-recognition-award":
      return SpecialRecognitionAwardPage;

    case "distinguished-distribution":
      return GovernanceAwardPage;

    case "special-edition":
      return SpecialEditionPage;
  }
};

/**
 * Returns the corresponding static component to render based on the provided active tab identifier.
 *
 * This function is typically used to dynamically render different sections of the
 * Sustainability Impact Awards (SIA) How to Nominate page.
 *
 * @param page - The string identifier of the active tab (e.g., "nominations", "assessment", "declarations").
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const renderSIAHowToNominateComponents = (tab: TabType) => {
  switch (tab) {
    case "nominationsTab":
      return NominationsSection;

    case "assessmentTab":
      return AssesmentSection;

    case "declarationsTab":
      return DeclarationSection;
  }
};

/**
 * Returns the corresponding formatted date for Header.
 *
 * This function is typically used to format the todays date in a readable format for
 * Sustainability Impact Awards (SIA) Header.
 *
 * @returns The formatted date string in 'EEE, dd MMM yyyy' format.
 */
export const formatDateToReadable = () => {
  const date = dayjs();

  return date.format("ddd, MMM DD, YYYY");
};

/**
 * Returns the page title based on the provided URL path.
 * This function is used to dynamically set the title for various static pages
 * within the Sustainability Impact Awards (SIA) section.
 * @param urlPath - The URL path of the page (e.g., "/events-awards/sustainability-impact-awards/about").
 * @returns The corresponding page title as a string.
 */
export const getStaticPageTitle = (urlPath: string) => {
  switch (urlPath) {
    case RouteFactory.siaAboutPage:
    case RouteFactory.sbaAboutPage:
    case RouteFactory.scaAboutPage:
    case RouteFactory.e50AboutPage:
    case RouteFactory.eeAboutPage:
      return "About,";

    case RouteFactory.siaAwardsPage:
    case RouteFactory.sbaAwardsPage:
    case RouteFactory.scaAwardsPage:
    case RouteFactory.e50AwardsPage:
    case RouteFactory.eeAwardsPage:
      return "Awards,";

    case RouteFactory.siaNominatePage:
    case RouteFactory.sbaNominatePage:
    case RouteFactory.scaNominatePage:
      return "How to Nominate,";

    case RouteFactory.siaContactUsPage:
    case RouteFactory.sbaContactUsPage:
    case RouteFactory.scaContactUsPage:
    case RouteFactory.e50ContactUsPage:
    case RouteFactory.eeContactUsPage:
      return "Contact Us,";

    case RouteFactory.siaPartnersPage:
    case RouteFactory.scaPartnersPage:
    case RouteFactory.eePartnersPage:
      return "Partners,";

    case RouteFactory.siaJudgesPage:
      return "Judges,";

    case RouteFactory.scaFaqPage:
    case RouteFactory.e50FaqPage:
    case RouteFactory.eeFaqPage:
      return "FAQ,";

    case RouteFactory.e50NominatePage:
    case RouteFactory.eeNominatePage:
      return "How to Apply,";

    case RouteFactory.eeApplyNowPage:
      return "Download Nomination Form,";

    default:
      return "THE BUSINESS TIMES";
  }
};

/** * Returns the page title for Events & Awards News pages based on award type.
 *
 * This function is used to dynamically set the title for various Events & Awards
 * News pages based on the award type.
 *
 * @param title - The title string from response.
 * @returns The corresponding news page title as a string.
 */
export const getEventAwardsNewsTitle = (title: string, awardType: string) => {
  switch (awardType) {
    case AwardType.SIA:
      return "SI Awards News Landing page, - THE BUSINESS TIMES";

    case AwardType.SBA:
      return "Singapore Business Awards Latest News & Headlines  - THE BUSINESS TIMES";

    case AwardType.SCA:
      return "Singapore Corporate Awards Latest News & Headlines  - THE BUSINESS TIMES";

    case AwardType.E50:
      return "Enterprise 50 News - THE BUSINESS TIMES";

    default:
      return title || "The Business Times";
  }
};

/**
 * Returns the card props for the Event Awards News component.
 * @param articles - The articles to be displayed in the component.
 * @param ref - The reference to be used for the link.
 * @returns {ComponentProps<typeof Card>[]} - The card props for the Top stories component.
 */
export const getEventAwardsNewsArticleProps = (
  articles: SanitizedFeedSectionArticle[]
): ComponentProps<typeof Card>[] => {
  const cardProps = articles.map(
    ({ id, title, urlPath, media, kicker, blurb, edited, created }) => {
      const _defaultImage = { directoryName: "events-awards" };
      const mainMedia = media[0];
      const imgSrc = mainMedia?.landscape?.url;
      const imgWidth = mainMedia?.landscape?.width;
      const imgHeight = mainMedia?.landscape?.height;

      const props: ComponentProps<typeof Card> = {
        id,
        title,
        link: urlPath,
        thumbnail:
          imgSrc && imgWidth && imgHeight
            ? {
                src: imgSrc,
                alt: mainMedia.alt || mainMedia.caption || "",
                width: imgWidth,
                height: imgHeight,
              }
            : undefined,
        kicker: kicker?.fields?.[0]?.value,
        defaultImage: _defaultImage,
        blurb,
        editedTime: edited,
        updatedTime: created,
      };

      return props;
    }
  );

  return JSON.parse(JSON.stringify(cardProps));
};

/**
 * Returns the card props for the Event Awards News Load More component.
 * @param articles - The articles to be displayed in the component.
 * @param ref - The reference to be used for the link.
 * @returns {ComponentProps<typeof Card>[]} - The card props for the Top stories component.
 */
export const getMoreNewsStoriesCardProps = (
  articles: SanitizedFeedSectionArticle[],
  variations?: CardVariations
): ComponentProps<typeof Card>[] => {
  return articles.map(
    ({ id, title, urlPath, media, kicker, blurb, created, edited }) => {
      const _defaultImage = { directoryName: "events-awards" };
      const mainMedia = media[0];
      const imgSrc = mainMedia?.landscape?.url;
      const imgWidth = mainMedia?.landscape?.width;
      const imgHeight = mainMedia?.landscape?.height;

      const props: ComponentProps<typeof Card> = {
        id,
        title,
        link: urlPath,
        thumbnail:
          imgSrc && imgWidth && imgHeight
            ? {
                src: imgSrc,
                alt: mainMedia.alt || mainMedia.caption || "",
                width: imgWidth,
                height: imgHeight,
              }
            : undefined,
        kicker: kicker?.fields?.[0]?.value,
        defaultImage: _defaultImage,
        variations,
        blurb,
        editedTime: edited,
        updatedTime: created,
      };

      return props;
    }
  );
};

/**
 * Returns the card props for the Event Awards SIA component.
 * @param articles - The articles to be displayed in the component.
 * @param ref - The reference to be used for the link.
 * @returns {ComponentProps<typeof Card>[]} - The card props for the Top stories component.
 */
export const getEventAwardsHomePageArticleProps = (
  articles: SanitizedFeedSectionArticle[]
): ComponentProps<typeof Card>[] => {
  const cardProps = articles.map(
    ({ id, title, urlPath, media, kicker, blurb }) => {
      const _defaultImage = { directoryName: "events-awards" };
      const mainMedia = media[0];
      const imgSrc = mainMedia?.landscape?.url;
      const imgWidth = mainMedia?.landscape?.width;
      const imgHeight = mainMedia?.landscape?.height;

      const props: ComponentProps<typeof Card> = {
        id,
        title,
        link: urlPath,
        thumbnail:
          imgSrc && imgWidth && imgHeight
            ? {
                src: imgSrc,
                alt: mainMedia.alt || mainMedia.caption || "",
                width: imgWidth,
                height: imgHeight,
              }
            : undefined,
        kicker: kicker?.fields?.[0]?.value,
        defaultImage: _defaultImage,
        blurb: blurb,
      };

      return props;
    }
  );

  return JSON.parse(JSON.stringify(cardProps));
};

/**
 * Returns the card props for the Event Awards page component.
 * @param articles - The articles to be displayed in the component.
 * @returns {ComponentProps<typeof Card>[]} - The card props for the Top stories component.
 */
export const getEventAwardsArticleProps = (
  articles: SanitizedFeedSectionArticle[]
): ComponentProps<typeof Card>[] => {
  const cardProps = articles.map(({ id, title, urlPath, media, kicker }) => {
    const _defaultImage = { directoryName: "events-awards" };
    const mainMedia = media[0];
    const imgSrc = mainMedia?.landscape?.url;
    const imgWidth = mainMedia?.landscape?.width;
    const imgHeight = mainMedia?.landscape?.height;

    const props: ComponentProps<typeof Card> = {
      id,
      title,
      link: urlPath,
      thumbnail:
        imgSrc && imgWidth && imgHeight
          ? {
              src: imgSrc,
              alt: mainMedia.alt || mainMedia.caption || "",
              width: imgWidth,
              height: imgHeight,
            }
          : undefined,
      kicker: kicker?.fields?.[0]?.value,
      defaultImage: _defaultImage,
    };

    return props;
  });

  return JSON.parse(JSON.stringify(cardProps));
};
